import React, { useState, useEffect } from "react";
import { useAuth } from "../services/auth.service";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faSearch, 
  faPlus, 
  faEdit, 
  faTrash, 
  faFileAlt,
  faSpinner,
  faFilter,
  faTimes,
  faImage,
  faVideo,
  faTag,
  faCog,
  faComment,
  faReply,
  faEye,
  faCheck
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  TextField, 
  IconButton,
  Chip,
  Avatar,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Snackbar,
  LinearProgress,
  Tooltip,
  Fab,
  Badge,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  overflow: 'hidden',
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef',
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: '#f8f9fa',
  borderRadius: '12px',
  padding: '8px 16px',
  border: '1px solid #e9ecef',
  transition: 'all 0.3s ease',
}));

const TemplateCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
  },
}));

export default function Template() {
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [modalResult, setModalResult] = useState("");
  const [templates, setTemplates] = useState([]);
  const [tempTemplates, setTempTemplates] = useState([]);
  const [currentFormAction, setCurrentFormAction] = useState("/");
  const [currentFormMethod, setCurrentFormMethod] = useState("POST");
  const [currentButtonText, setCurrentButtonText] = useState("Guardar");
  const [currentTitle, setCurrentTitle] = useState("Nueva Plantilla");
  const [modalBody, setModalBody] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user, logout, currentConfig, changeCurrentConfig, getCurrentConfig } = useAuth();
  const [searchValue, setSearchValue] = useState("");
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({});
  
  // New WhatsApp Cloud API states
  const [waTemplates, setWaTemplates] = useState([]);
  const [templateStats, setTemplateStats] = useState({
    total: 0,
    approved: 0,
    pending: 0,
    rejected: 0,
    disabled: 0
  });
  const [syncLoading, setSyncLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showWaTemplates, setShowWaTemplates] = useState(false);
  const [useImageUpload, setUseImageUpload] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [parameterChips, setParameterChips] = useState([]);
  const [variableSourceTable, setVariableSourceTable] = useState("renovacion");
  const [variableFieldOptions, setVariableFieldOptions] = useState([]);
  const [variableFieldsLoading, setVariableFieldsLoading] = useState(false);
  const [comparacionValores, setComparacionValores] = useState([]);
  const [ejecutivoFieldOptions, setEjecutivoFieldOptions] = useState([]);
  const [ejecutivoOptions, setEjecutivoOptions] = useState([]);
  const [compositeVariables, setCompositeVariables] = useState([]);
  const [fixedVariableValues, setFixedVariableValues] = useState({});
  const [asignacionEjecutivo, setAsignacionEjecutivo] = useState({
    variableEjecutivoNombre: "",
    campoEjecutivoId: "",
    fallbackEjecutivoId: "",
    variableNombreCliente: "",
  });

  const parseFieldValues = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value
        .map((item) => (typeof item === "string" ? item : item?.value))
        .filter(Boolean);
    }
    if (typeof value === "string") {
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
    return [];
  };

  const extractVariables = (text = "") => {
    const matches = [...text.matchAll(/\{\{(\d+)\}\}/g)].map((match) => Number(match[1]));
    return [...new Set(matches)].sort((a, b) => a - b);
  };

  const normalizeComparisonRules = (rules) => {
    if (!Array.isArray(rules)) return [];
    return rules
      .map((rule) => ({
        variableOrigen: Number(rule?.variableOrigen || 0),
        campoComparacionEjecutivo: `${rule?.campoComparacionEjecutivo || ""}`.trim(),
        variableDestino: Number(rule?.variableDestino || 0),
        campoResultadoEjecutivo: `${rule?.campoResultadoEjecutivo || ""}`.trim(),
      }))
      .filter((rule) =>
        rule.variableOrigen > 0 &&
        rule.variableDestino > 0 &&
        rule.campoComparacionEjecutivo &&
        rule.campoResultadoEjecutivo
      );
  };

  const normalizeExecutiveAssignment = (assignment) => {
    return {
      variableEjecutivoNombre: Number(assignment?.variableEjecutivoNombre || 0) || "",
      campoEjecutivoId: `${assignment?.campoEjecutivoId || ""}`.trim(),
      fallbackEjecutivoId: `${assignment?.fallbackEjecutivoId || ""}`.trim(),
      variableNombreCliente: Number(assignment?.variableNombreCliente || 0) || "",
    };
  };

  const normalizeCompositeVariables = (items) => {
    if (!Array.isArray(items)) return [];
    return items
      .map((item) => ({
        name: `${item?.name || ""}`.trim(),
        expression: `${item?.expression || ""}`.trim(),
      }))
      .filter((item) => item.name && item.expression);
  };

  const renderCompositeExpressionPreview = (expression) => {
    if (!expression) return "";
    return `${expression}`.replace(/\{([^}]+)\}/g, (_, fieldName) => `[${`${fieldName}`.trim()}]`);
  };

  const parseFixedVariableValues = (value) => {
    if (!value) return {};
    if (Array.isArray(value)) {
      return value.reduce((acc, item) => {
        const token = Number(item?.variable || item?.token || 0);
        const fixedValue = `${item?.value || ""}`.trim();
        if (token > 0 && fixedValue) acc[token] = fixedValue;
        return acc;
      }, {});
    }
    if (typeof value === "object") {
      return Object.keys(value).reduce((acc, key) => {
        const token = Number(key);
        const fixedValue = `${value[key] || ""}`.trim();
        if (token > 0 && fixedValue) acc[token] = fixedValue;
        return acc;
      }, {});
    }
    return {};
  };

  const normalizeFixedVariableValues = (valueMap = {}) =>
    Object.keys(valueMap)
      .map((key) => ({
        variable: Number(key),
        value: `${valueMap[key] || ""}`.trim(),
      }))
      .filter((item) => item.variable > 0 && item.value);

  const defaultModalBody = [
    {
      id: "name",
      name: "name",
      type: "text",
      placeholder: "Nombre_Plantilla",
      value: "",
    },
    {
      id: "body",
      name: "body",
      type: "textarea",
      placeholder: "_*Hola*_\n\nLe informamos que fue APROBADO el trámite\nAsegurado *{{1}}*\nCédula *{{2}}*\nIniciado el día *{{3}}*\nSuma asegurada $*{{4}}*\n\n*_Gracias_*",
      value: "",
    },
    {
      id: "source",
      name: "source",
      type: "select-datasource",
      dataSource: "/sources",
      multiselect: false,
      value: "",
      defaultValue: "",
      limit: 1,
      placeholder: "Plantilla Fuente",
      nextSourceIds: ["parameters", "buttonparameters", "phoneparameter", "agentparameter"],
    },
    {
      id: "parameters",
      name: "parameters",
      type: "select",
      dataSource: "/renovaciones/polizas/columns",
      multiselect: true,
      value: "",
      defaultValue: [],
      limit: 7,
      placeholder: "Parametros",
    },
    {
      id: "image-video-select",
      name: "image-video-select",
      type: "select-show",
      defaultValue: [{ value: "image", label: "image" }],
      value: [{ value: "image", label: "image" }],
      optionIds: ["image", "video"]
    },
    {
      id: "image",
      name: "image",
      type: "text",
      placeholder: "https://ejemplo.com/imagen.png",
      value: "",
    },
    {
      id: "video",
      name: "video",
      type: "hidden",
      placeholder: "https://ejemplo.com/video.mp4",
      value: "",
    },
    {
      id: "buttonparameters",
      name: "buttonparameters",
      type: "select",
      dataSource: "/renovaciones/polizas/columns",
      multiselect: true,
      value: "",
      defaultValue: [],
      limit: 2,
      placeholder: "Parametros_Boton",
    },
    {
      id: "buttonparameterindex",
      name: "buttonparameterindex",
      type: "number",
      placeholder: "1 o 0",
      value: "0",
    },
    {
      id: "agentbuttonparameters",
      name: "agentbuttonparameters",
      type: "select",
      dataSource: "/template?listnames=1",
      multiselect: true,
      value: "",
      limit: 1,
      placeholder: "Parametros_Boton_Agente",
    },
    {
      id: "agentbuttonparameterindex",
      name: "agentbuttonparameterindex",
      type: "number",
      placeholder: "1 o 0",
      value: "0",
    },
    {
      id: "phoneparameter",
      name: "phoneparameter",
      type: "select",
      dataSource: "/renovaciones/polizas/columns",
      multiselect: false,
      value: "",
      limit: 1,
      placeholder: "Columna_Telefono",
    },
    {
      id: "agentparameter",
      name: "agentparameter",
      type: "select",
      dataSource: "/renovaciones/polizas/columns",
      multiselect: false,
      value: "",
      limit: 1,
      placeholder: "Columna_Agente",
    },
    {
      id: "collection",
      name: "collection",
      type: "hidden",
      value: "renovacion",
      defaultValue: "renovacion",
    },
    {
      id: "is_monday",
      name: "is_monday",
      type: "checkbox",
      value: false,
      placeholder: "Es plantilla de Monday?",
    },
    {
      id: "is_flow",
      name: "is_flow",
      type: "checkbox",
      value: false,
      placeholder: "Es plantilla de flow?",
    },
  ];

  const customActionTemplate = [
    {
      id: "name",
      name: "name",
      type: "text",
      placeholder: "Nombre_Plantilla",
      value: "",
    },
    {
      id: "body",
      name: "body",
      type: "textarea",
      placeholder: "_*Hola*_\n\nLe informamos que fue APROBADO el trámite\nAsegurado *{{1}}*\nCédula *{{2}}*\nIniciado el día *{{3}}*\nSuma asegurada $*{{4}}*\n\n*_Gracias_*",
      value: "",
    },
    {
      id: "parameters",
      name: "parameters",
      type: "select",
      dataSource: "/renovaciones/polizas/columns",
      multiselect: true,
      value: "",
      defaultValue: [],
      limit: 7,
      placeholder: "Parametros",
    },
    {
      id: "image",
      name: "image",
      type: "text",
      placeholder: "https://ejemplo.com/imagen.png",
      value: "",
    },
    {
      id: "buttonparameters",
      name: "buttonparameters",
      type: "select",
      dataSource: "/template?listnames=1",
      multiselect: true,
      value: "",
      limit: 2,
      placeholder: "Parametros_Boton",
    },
    {
      id: "buttonparameterindex",
      name: "buttonparameterindex",
      type: "number",
      placeholder: "1 o 0",
      value: "0",
    },
    {
      id: "agentbuttonparameters",
      name: "agentbuttonparameters",
      type: "select",
      dataSource: "/template?listnames=1",
      multiselect: true,
      value: "",
      limit: 1,
      placeholder: "Parametros_Boton_Agente",
    },
    {
      id: "agentbuttonparameterindex",
      name: "agentbuttonparameterindex",
      type: "number",
      placeholder: "1 o 0",
      value: "0",
    },
    {
      id: "template_origin",
      name: "template_origin",
      type: "select",
      dataSource: "/collections",
      multiselect: false,
      value: "",
      defaultValue: "",
      limit: 1,
      placeholder: "Coleccion",
    },
    {
      id: "type",
      name: "type",
      type: "hidden",
      value: "quickreply",
      defaultValue: "quickreply",
    },
    {
      id: "collection",
      name: "collection",
      type: "hidden",
      value: "renovacion",
      defaultValue: "renovacion",
    },
    {
      id: "is_monday",
      name: "is_monday",
      type: "checkbox",
      value: false,
      placeholder: "Es plantilla de Monday?",
    },
    {
      id: "is_flow",
      name: "is_flow",
      type: "checkbox",
      value: false,
      placeholder: "Es plantilla de flow?",
    },
  ];

  const searchTemplate = (e) => {
    setSearchValue(e.target.value);
    if (e.target.value === "") {
      setTemplates(tempTemplates);
    } else {
      setTemplates(
        tempTemplates.filter((template) =>
          template.name.toLowerCase().includes(e.target.value.toLowerCase())
        )
      );
    }
  };

  useEffect(() => {
    if (loading) return;
    async function fetchData() {
      setLoading(true);
      const url = process.env.REACT_APP_BACKEND_API + "/template";
      let config = {
        headers: {
          "x-access-token": user.accessToken,
          "Content-Type": "application/json",
        },
      }
      let result = await axios.get(url, config)
        .then((response) => {
          return response;
        }
        )
        .catch((error) => {
          return error;
        }
        );
      if (result.data) {
        setLoading(false);
        setTemplates(result.data);
        setTempTemplates(result.data);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    if (!dialogOpen || !user?.accessToken) return;

    const fetchVariableFieldOptions = async () => {
      setVariableFieldsLoading(true);
      try {
        const response = await axios.get(
          process.env.REACT_APP_BACKEND_API +
            `/customtablecolumns?tablename=${encodeURIComponent(variableSourceTable)}`,
          {
            headers: {
              "x-access-token": user.accessToken,
              "Content-Type": "application/json",
            },
          }
        );
        const options = Array.isArray(response.data)
          ? response.data
              .map((item) => item?._id)
              .filter(Boolean)
          : [];
        setVariableFieldOptions(options);
      } catch (error) {
        setVariableFieldOptions([]);
        setSnackbar({
          open: true,
          message: "No se pudieron cargar campos sugeridos",
          severity: "warning",
        });
      } finally {
        setVariableFieldsLoading(false);
      }
    };

    fetchVariableFieldOptions();
  }, [dialogOpen, variableSourceTable, user?.accessToken]);

  useEffect(() => {
    if (!dialogOpen || !user?.accessToken) return;

    const fetchEjecutivoFields = async () => {
      try {
        const baseApi = `${process.env.REACT_APP_BACKEND_API || ""}`.replace(/\/+$/, "");
        const fallbackBase = baseApi.replace(/\/api$/, "");
        const ejecutivoUrls = Array.from(
          new Set([`${baseApi}/ejecutivo`, `${fallbackBase}/api/ejecutivo`])
        );
        const headers = {
          "x-access-token": user.accessToken,
          "Content-Type": "application/json",
        };
        let response = null;
        for (const url of ejecutivoUrls) {
          const attempt = await axios.get(url, { headers }).catch(() => null);
          if (attempt?.data) {
            response = attempt;
            break;
          }
        }
        const firstEjecutivo = Array.isArray(response?.data) && response.data.length > 0
          ? response.data[0]
          : null;
        const options = Array.isArray(response?.data)
          ? response.data
              .map((item) => ({
                value: `${item?._id || ""}`.trim(),
                label: `${item?.firstName || ""} ${item?.lastName || ""}`.trim() || `${item?._id || ""}`,
              }))
              .filter((item) => item.value)
          : [];
        setEjecutivoOptions(options);
        if (!firstEjecutivo) {
          setEjecutivoFieldOptions([]);
          return;
        }
        const fields = Object.keys(firstEjecutivo).filter(
          (key) =>
            key !== "password" &&
            typeof firstEjecutivo[key] !== "object"
        );
        setEjecutivoFieldOptions(fields);
      } catch (error) {
        setEjecutivoFieldOptions([]);
        setEjecutivoOptions([]);
      }
    };

    fetchEjecutivoFields();
  }, [dialogOpen, user?.accessToken]);

  const formOnChange = (e, action) => {
    try {
      const { name, value } = e.target;
      setModalBody(
        modalBody.map((item) => {
          if (item.name === name) {
            item.value = value;
          }
          return item;
        })
      );
    } catch (error) {
      const items = e;
      setModalBody(
        modalBody.map((bodyItem) => {
          if (
            (bodyItem.type === "select" || bodyItem.type === "select-show" || bodyItem.type === "select-datasource") &&
            bodyItem.name === action.name
          ) {
            bodyItem.value = items;
            return bodyItem;
          }
          return bodyItem;
        })
      );
    }
  };

  const newTemplate = (e) => {
    e.preventDefault();
    setModalBody(defaultModalBody);
    setCurrentTitle("Nueva Plantilla");
    setCurrentFormAction("/");
    setCurrentFormMethod("POST");
    setCurrentButtonText("Guardar");
    setFormData({});
    setParameterChips([]);
    setCompositeVariables([]);
    setComparacionValores([]);
    setFixedVariableValues({});
    setAsignacionEjecutivo({
      variableEjecutivoNombre: "",
      campoEjecutivoId: "",
      fallbackEjecutivoId: "",
      variableNombreCliente: "",
    });
    setVariableSourceTable("renovacion");
    setUseImageUpload(false);
    setDialogOpen(true);
  };

  const newTemplateQuickReply = (e) => {
    e.preventDefault();
    setModalBody(customActionTemplate);
    setCurrentTitle("Nueva Plantilla Quick Reply");
    setCurrentFormAction("/");
    setCurrentFormMethod("POST");
    setCurrentButtonText("Guardar");
    setFormData({});
    setParameterChips([]);
    setCompositeVariables([]);
    setComparacionValores([]);
    setFixedVariableValues({});
    setAsignacionEjecutivo({
      variableEjecutivoNombre: "",
      campoEjecutivoId: "",
      fallbackEjecutivoId: "",
      variableNombreCliente: "",
    });
    setVariableSourceTable("renovacion");
    setUseImageUpload(false);
    setDialogOpen(true);
  };

  const deleteTemplate = (e) => {
    e.preventDefault();
    const id = templates[e.currentTarget.dataset.id]._id;
    fetch(process.env.REACT_APP_BACKEND_API + "/template/" + id, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        'x-access-token': user.accessToken,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        console.log(data);
        setTemplates(templates.filter((template) => template._id !== id));
        setTempTemplates(tempTemplates.filter((template) => template._id !== id));
        setSnackbar({ open: true, message: 'Template eliminado correctamente', severity: 'success' });
      })
      .catch((error) => {
        setSnackbar({ open: true, message: 'Error al eliminar template', severity: 'error' });
      });
  };

  const editTemplate = (e) => {
    e.preventDefault();
    const template = templates[e.currentTarget.dataset.id];
    const selectedModalBody = template.type === "quickreply" ? customActionTemplate : defaultModalBody;
    setCurrentFormAction("/" + template._id);
    setCurrentFormMethod("PUT");
    setCurrentButtonText("Actualizar");
    setCurrentTitle(template.type === "quickreply" ? "Editar Plantilla Quick Reply" : "Editar Plantilla");
    setModalBody(selectedModalBody);
    
    // Convert template data to form data
    const formDataObj = {};
    selectedModalBody.forEach(item => {
      const templateValue = template[item.name];
      formDataObj[item.name] = templateValue !== undefined ? templateValue : (item.value || "");
    });
    setFormData(formDataObj);
    setParameterChips(parseFieldValues(formDataObj.parameters));
    setCompositeVariables(normalizeCompositeVariables(template.compositeVariables));
    setComparacionValores(normalizeComparisonRules(template.comparacionValores));
    setFixedVariableValues(parseFixedVariableValues(template.fixedVariableValues || template.fixedVariables));
    setAsignacionEjecutivo(normalizeExecutiveAssignment(template.asignacionEjecutivo));
    setVariableSourceTable(formDataObj.collection || "renovacion");
    setUseImageUpload(false);
    setDialogOpen(true);
  };

  const uploadTemplateImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImageUploading(true);
    try {
      const payload = new FormData();
      payload.append("file", file);

      const response = await axios.post(
        process.env.REACT_APP_BACKEND_API + "/template-images/upload",
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "x-access-token": user.accessToken,
            Authorization: `Bearer ${user.accessToken}`,
          },
        }
      );

      const imageUrl = response?.data?.public_url || response?.data?.url || "";
      if (imageUrl) {
        setFormData((prev) => ({ ...prev, image: imageUrl }));
        setSnackbar({ open: true, message: "Imagen subida correctamente", severity: "success" });
      } else {
        setSnackbar({ open: true, message: "No se recibió URL de imagen", severity: "error" });
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Error subiendo imagen (max 5MB)", severity: "error" });
    } finally {
      setImageUploading(false);
      event.target.value = "";
    }
  };

  const cleanModalForm = () => {
    setModalBody(
      modalBody.map((item) => {
        item.value = "";
        return item;
      })
    );
  };

  const submitTemplate = (e) => {
    e.preventDefault();
    const missingVariableAssignments = unassignedVariableTokens.filter(
      (token) => !`${fixedVariableValues[token] || ""}`.trim()
    );
    if (missingVariableAssignments.length > 0) {
      setSnackbar({
        open: true,
        message: `Las variables ${missingVariableAssignments
          .map((token) => `{{${token}}}`)
          .join(", ")} no tienen campo asignado ni valor fijo.`,
        severity: "warning",
      });
      return;
    }
    setModalSubmitting(true);
    
    // Convert form data to modal body format
    const updatedModalBody = modalBody.map(item => ({
      ...item,
      value: formData[item.name] || item.value
    }));
    
    const value = updatedModalBody.reduce((acc, item) => {
      acc[item.name] = item.value;
      return acc;
    }, {});

    value.parameters = parameterChips
      .map((param) => `${param}`.trim())
      .filter(Boolean)
      .map((param) => ({ value: param }));
    value.compositeVariables = normalizeCompositeVariables(compositeVariables);
    value.comparacionValores = normalizeComparisonRules(comparacionValores);
    value.fixedVariableValues = normalizeFixedVariableValues(fixedVariableValues);
    value.asignacionEjecutivo = normalizeExecutiveAssignment(asignacionEjecutivo);
    
    //send the data post to endpoint /template
    fetch(process.env.REACT_APP_BACKEND_API + "/template" + currentFormAction, {
      method: currentFormMethod,
      headers: {
        "Content-Type": "application/json",
        'x-access-token': user.accessToken,
      },
      body: JSON.stringify(value),
    })
      .then((res) => res.json())
      .then((data) => {
        console.log(data);
        if (currentFormMethod == "POST") {
          setTemplates([...templates, data]);
          setTempTemplates([...tempTemplates, data]);
          setSnackbar({ open: true, message: 'Template creado correctamente', severity: 'success' });
        } else if (currentFormMethod == "PUT") {
          setTemplates(
            templates.map((template) => {
              if (template._id === data._id) {
                //only update the fields that are not empty
                for (const key in data) {
                  if (data[key] !== "") {
                    template[key] = data[key];
                  }
                }
                return template;
              }
              return template;
            })
          );
          setTempTemplates(
            templates.map((template) => {
              if (template._id === data._id) {
                //only update the fields that are not empty
                for (const key in data) {
                  if (data[key] !== "") {
                    template[key] = data[key];
                  }
                }
                return template;
              }
              return template;
            })
          );
          setSnackbar({ open: true, message: 'Template actualizado correctamente', severity: 'success' });
        }
        setDialogOpen(false);
        setFormData({});
        cleanModalForm();
        setModalSubmitting(false);
      })
      .catch((err) => {
        console.log(err);
        setSnackbar({ open: true, message: 'Error al procesar template', severity: 'error' });
        setModalSubmitting(false);
      });
  };

  const getTemplateStats = () => {
    const total = templates.length;
    const quickReply = templates.filter(t => t.type === 'quickreply').length;
    const button = templates.filter(t => !t.type || t.type === 'button').length;
    const withImage = templates.filter(t => t.image).length;
    
    return { total, quickReply, button, withImage };
  };

  const stats = getTemplateStats();

  const truncateText = (text, maxLength = 50) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const insertVariable = () => {
    const bodyText = formData.body || "";
    const variables = extractVariables(bodyText);
    const nextVariable = variables.length ? Math.max(...variables) + 1 : 1;
    const token = `{{${nextVariable}}}`;
    const separator = bodyText && !bodyText.endsWith(" ") ? " " : "";
    setFormData({ ...formData, body: `${bodyText}${separator}${token}`.trim() });
  };

  const addComparacionValor = () => {
    const availableToken = variableTokens[0] || 1;
    const firstEjecutivoField = ejecutivoFieldOptions[0] || "";
    setComparacionValores((prev) => [
      ...prev,
      {
        variableOrigen: availableToken,
        campoComparacionEjecutivo: firstEjecutivoField,
        variableDestino: availableToken,
        campoResultadoEjecutivo: firstEjecutivoField,
      },
    ]);
  };

  const updateComparacionValor = (index, key, value) => {
    setComparacionValores((prev) =>
      prev.map((item, itemIndex) => {
        if (itemIndex !== index) return item;
        return {
          ...item,
          [key]: key.includes("variable") ? Number(value) : value,
        };
      })
    );
  };

  const removeComparacionValor = (index) => {
    setComparacionValores((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const addCompositeVariable = () => {
    setCompositeVariables((prev) => [
      ...prev,
      {
        name: "",
        expression: "",
      },
    ]);
  };

  const updateCompositeVariable = (index, key, value) => {
    setCompositeVariables((prev) =>
      prev.map((item, itemIndex) => (itemIndex === index ? { ...item, [key]: value } : item))
    );
  };

  const removeCompositeVariable = (index) => {
    setCompositeVariables((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const variableTokens = extractVariables(formData.body || "");
  const normalizedComparisonRules = normalizeComparisonRules(comparacionValores);
  const coveredByParameterSet = new Set(
    variableTokens.filter((token) => `${parameterChips[token - 1] || ""}`.trim())
  );
  const coveredByComparisonSet = new Set(
    normalizedComparisonRules.flatMap((rule) => [rule.variableOrigen, rule.variableDestino])
  );
  const unassignedVariableTokens = variableTokens.filter(
    (token) => !coveredByParameterSet.has(token) && !coveredByComparisonSet.has(token)
  );
  const compositeVariableMap = normalizeCompositeVariables(compositeVariables).reduce((acc, item) => {
    acc[item.name] = item.expression;
    return acc;
  }, {});
  const variableValueOptions = Array.from(
    new Set([
      ...variableFieldOptions,
      ...Object.keys(compositeVariableMap),
    ])
  );
  const variableTokenOptions = variableTokens.length
    ? variableTokens
    : Array.from({ length: Math.max(1, parameterChips.length) }, (_, index) => index + 1);
  const parameterValues = parameterChips.map((param) => {
    const expression = compositeVariableMap[param];
    return expression ? renderCompositeExpressionPreview(expression) : param;
  });
  const previewBody = (formData.body || "").replace(/\{\{(\d+)\}\}/g, (_, num) => {
    const index = Number(num) - 1;
    const fixedValue = `${fixedVariableValues[Number(num)] || ""}`.trim();
    return parameterValues[index] || fixedValue || `{{${num}}}`;
  });

  useEffect(() => {
    setFixedVariableValues((prev) => {
      const allowed = new Set(variableTokens);
      const next = {};
      Object.keys(prev).forEach((key) => {
        if (allowed.has(Number(key))) next[key] = prev[key];
      });
      return next;
    });
  }, [formData.body]);

  // WhatsApp Cloud API functions
  const fetchWaTemplates = async () => {
    // Endpoint removed on backend; keep local list empty to avoid 404 calls.
    setWaTemplates([]);
  };

  const fetchTemplateStats = async () => {
    // Endpoint removed on backend; derive stats from local templates list.
    setTemplateStats({
      total: templates.length,
      approved: 0,
      pending: 0,
      rejected: 0,
      disabled: 0
    });
  };

  const syncTemplates = async () => {
    setSyncLoading(true);
    try {
      const response = await axios.post(process.env.REACT_APP_BACKEND_API + "/api/wa-templates/sync", {}, {
        headers: {
          "x-access-token": user.accessToken,
          "Content-Type": "application/json",
        },
      });
      setSnackbar({ open: true, message: `Sincronizados ${response.data.synced} templates`, severity: 'success' });
      fetchWaTemplates();
    } catch (error) {
      console.error('Error syncing templates:', error);
      setSnackbar({ open: true, message: 'Error al sincronizar templates', severity: 'error' });
    } finally {
      setSyncLoading(false);
    }
  };

  const checkTemplateStatus = async (templateName) => {
    try {
      const response = await axios.get(process.env.REACT_APP_BACKEND_API + `/api/wa-templates/check/${templateName}`, {
        headers: {
          "x-access-token": user.accessToken,
          "Content-Type": "application/json",
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error checking template status:', error);
      return { approved: false, template: null };
    }
  };

  const getTemplatesByStatus = async (status) => {
    try {
      const response = await axios.get(process.env.REACT_APP_BACKEND_API + `/api/wa-templates/status/${status}`, {
        headers: {
          "x-access-token": user.accessToken,
          "Content-Type": "application/json",
        },
      });
      return response.data.templates || [];
    } catch (error) {
      console.error('Error fetching templates by status:', error);
      return [];
    }
  };

  // Load WhatsApp templates and stats on component mount
  useEffect(() => {
    fetchTemplateStats();
  }, [templates]);

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#2c3e50', mb: 1 }}>
          Templates de WhatsApp
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Gestiona y edita plantillas de WhatsApp Business para tus campañas
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Total Templates
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <FontAwesomeIcon icon={faFileAlt} size="lg" />
                </Avatar>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.button}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Templates con Botones
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <FontAwesomeIcon icon={faComment} size="lg" />
                </Avatar>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.quickReply}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Respuestas Rápidas
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <FontAwesomeIcon icon={faReply} size="lg" />
                </Avatar>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.withImage}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Con Imágenes
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <FontAwesomeIcon icon={faImage} size="lg" />
                </Avatar>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Search and Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <SearchContainer sx={{ flex: 1, maxWidth: 400 }}>
          <FontAwesomeIcon icon={faSearch} style={{ color: '#6c757d', marginRight: '8px' }} />
          <TextField
            fullWidth
            placeholder="Buscar templates..."
            value={searchValue}
            onChange={searchTemplate}
            variant="standard"
            InputProps={{ disableUnderline: true }}
          />
        </SearchContainer>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* WhatsApp Cloud API Controls */}
          <Button
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={showWaTemplates ? faEye : faEye} />}
            onClick={() => setShowWaTemplates(!showWaTemplates)}
            sx={{ borderRadius: '8px' }}
          >
            {showWaTemplates ? 'Templates Locales' : 'Templates WhatsApp'}
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={syncLoading ? faSpinner : faCog} spin={syncLoading} />}
            onClick={syncTemplates}
            disabled={syncLoading}
            sx={{ borderRadius: '8px' }}
          >
            {syncLoading ? 'Sincronizando...' : 'Sincronizar'}
          </Button>

          <Button
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={viewMode === 'table' ? faEye : faFilter} />}
            onClick={() => setViewMode(viewMode === 'table' ? 'grid' : 'table')}
            sx={{ borderRadius: '8px' }}
          >
            {viewMode === 'table' ? 'Vista Grid' : 'Vista Tabla'}
          </Button>
          
          <Button
            variant="contained"
            startIcon={<FontAwesomeIcon icon={faPlus} />}
            onClick={newTemplate}
            sx={{ 
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              }
            }}
          >
            Nueva Plantilla
          </Button>
          <Button
            variant="contained"
            startIcon={<FontAwesomeIcon icon={faReply} />}
            onClick={newTemplateQuickReply}
            sx={{ 
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #218838 0%, #1ea085 100%)',
              }
            }}
          >
            Quick Reply
          </Button>
        </Box>
      </Box>

      {/* WhatsApp Template Status Filter */}
      {showWaTemplates && (
        <Box sx={{ mb: 3, p: 2, backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Estado de Templates WhatsApp
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip 
              label={`Total: ${templateStats.total}`} 
              color="primary" 
              variant="outlined"
            />
            <Chip 
              label={`Aprobados: ${templateStats.approved}`} 
              color="success" 
              variant="outlined"
            />
            <Chip 
              label={`Pendientes: ${templateStats.pending}`} 
              color="warning" 
              variant="outlined"
            />
            <Chip 
              label={`Rechazados: ${templateStats.rejected}`} 
              color="error" 
              variant="outlined"
            />
            <Chip 
              label={`Deshabilitados: ${templateStats.disabled}`} 
              color="default" 
              variant="outlined"
            />
          </Box>
        </Box>
      )}

      {/* Content */}
      {viewMode === 'table' ? (
        /* Table View */
        <StyledTableContainer component={Paper}>
          {loading && <LinearProgress />}
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell>Nombre</StyledTableCell>
                <StyledTableCell>Tipo</StyledTableCell>
                <StyledTableCell>Body</StyledTableCell>
                <StyledTableCell>Imagen</StyledTableCell>
                <StyledTableCell>Parámetros</StyledTableCell>
                <StyledTableCell>Botones</StyledTableCell>
                <StyledTableCell align="center">Acciones</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      <Typography>Cargando templates...</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : templates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <FontAwesomeIcon icon={faFileAlt} size="2x" style={{ color: '#ccc' }} />
                      <Typography variant="body1" color="text.secondary">
                        No se encontraron templates
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {searchValue ? 'Intenta ajustar tus criterios de búsqueda' : 'Crea tu primer template usando el botón de arriba'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                templates.map((template, key) => (
                  <TableRow key={template._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: template.type === 'quickreply' ? '#28a745' : '#667eea' }}>
                          <FontAwesomeIcon icon={template.type === 'quickreply' ? faReply : faComment} />
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {template.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={template.type ? template.type : "button"} 
                        color={template.type === 'quickreply' ? 'success' : 'primary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {truncateText(template.body, 60)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {template.image ? (
                        <Box sx={{ width: 60, height: 60, borderRadius: 1, overflow: 'hidden' }}>
                          <img 
                            src={template.image} 
                            alt={template.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Sin imagen
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {template.parameters ? 
                          template.parameters.slice(0, 3).map((param, idx) => (
                            <Chip key={idx} label={param.value} size="small" variant="outlined" />
                          ))
                          : <Typography variant="body2" color="text.secondary">Sin parámetros</Typography>
                        }
                        {template.parameters && template.parameters.length > 3 && (
                          <Chip label={`+${template.parameters.length - 3}`} size="small" />
                        )}
                        {Array.isArray(template.fixedVariableValues) && template.fixedVariableValues.length > 0 && (
                          <Chip
                            label={`Fijos: ${template.fixedVariableValues.length}`}
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {template.buttonparameters ? 
                          template.buttonparameters.slice(0, 2).map((param, idx) => (
                            <Chip key={idx} label={param.value} size="small" variant="outlined" color="secondary" />
                          ))
                          : <Typography variant="body2" color="text.secondary">Sin botones</Typography>
                        }
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="Editar">
                          <IconButton
                            onClick={editTemplate}
                            data-id={key}
                            size="small"
                            sx={{ color: '#667eea' }}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton
                            onClick={deleteTemplate}
                            data-id={key}
                            size="small"
                            sx={{ color: '#dc3545' }}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>
      ) : (
        /* Grid View */
        <Grid container spacing={3}>
          {loading ? (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4, gap: 2 }}>
                <FontAwesomeIcon icon={faSpinner} spin />
                <Typography>Cargando templates...</Typography>
              </Box>
            </Grid>
          ) : templates.length === 0 ? (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
                <FontAwesomeIcon icon={faFileAlt} size="2x" style={{ color: '#ccc' }} />
                <Typography variant="body1" color="text.secondary">
                  No se encontraron templates
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchValue ? 'Intenta ajustar tus criterios de búsqueda' : 'Crea tu primer template usando el botón de arriba'}
                </Typography>
              </Box>
            </Grid>
          ) : (
            templates.map((template, key) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={template._id}>
                <TemplateCard>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ bgcolor: template.type === 'quickreply' ? '#28a745' : '#667eea', width: 32, height: 32 }}>
                          <FontAwesomeIcon icon={template.type === 'quickreply' ? faReply : faComment} size="sm" />
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {template.name}
                        </Typography>
                      </Box>
                      <Chip 
                        label={template.type ? template.type : "button"} 
                        color={template.type === 'quickreply' ? 'success' : 'primary'}
                        size="small"
                      />
                    </Box>
                    
                    {template.image && (
                      <Box sx={{ mb: 2, borderRadius: 1, overflow: 'hidden', height: 120 }}>
                        <img 
                          src={template.image} 
                          alt={template.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </Box>
                    )}
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                      {truncateText(template.body, 80)}
                    </Typography>
                    
                    <Divider sx={{ my: 1 }} />
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                      {template.parameters ? 
                        template.parameters.slice(0, 2).map((param, idx) => (
                          <Chip key={idx} label={param.value} size="small" variant="outlined" />
                        ))
                        : <Typography variant="caption" color="text.secondary">Sin parámetros</Typography>
                      }
                      {Array.isArray(template.fixedVariableValues) && template.fixedVariableValues.length > 0 && (
                        <Chip label={`Fijos: ${template.fixedVariableValues.length}`} size="small" color="warning" variant="outlined" />
                      )}
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        {template.created_at}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Editar">
                          <IconButton
                            onClick={editTemplate}
                            data-id={key}
                            size="small"
                            sx={{ color: '#667eea' }}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton
                            onClick={deleteTemplate}
                            data-id={key}
                            size="small"
                            sx={{ color: '#dc3545' }}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </CardContent>
                </TemplateCard>
              </Grid>
            ))
          )}
        </Grid>
      )}

      {/* WhatsApp Templates Section */}
      {showWaTemplates && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#2c3e50', mb: 3 }}>
            Templates de WhatsApp Cloud API
          </Typography>
          
          {viewMode === 'table' ? (
            /* WhatsApp Templates Table View */
            <StyledTableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Nombre</StyledTableCell>
                    <StyledTableCell>Estado</StyledTableCell>
                    <StyledTableCell>Categoría</StyledTableCell>
                    <StyledTableCell>Idioma</StyledTableCell>
                    <StyledTableCell>Componentes</StyledTableCell>
                    <StyledTableCell>Última Actualización</StyledTableCell>
                    <StyledTableCell align="center">Acciones</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {waTemplates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                          <FontAwesomeIcon icon={faFileAlt} size="2x" style={{ color: '#ccc' }} />
                          <Typography variant="body1" color="text.secondary">
                            No se encontraron templates de WhatsApp
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Usa el botón "Sincronizar" para cargar templates desde WhatsApp Cloud API
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    waTemplates.map((template, key) => (
                      <TableRow key={template.wa_template_id || key} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ 
                              bgcolor: template.status === 'APPROVED' ? '#28a745' : 
                                       template.status === 'PENDING' ? '#ffc107' : 
                                       template.status === 'REJECTED' ? '#dc3545' : '#6c757d' 
                            }}>
                              <FontAwesomeIcon icon={faComment} />
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {template.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={template.status} 
                            color={
                              template.status === 'APPROVED' ? 'success' : 
                              template.status === 'PENDING' ? 'warning' : 
                              template.status === 'REJECTED' ? 'error' : 'default'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={template.category} 
                            color="primary" 
                            variant="outlined"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {template.language}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {template.components ? 
                              template.components.slice(0, 2).map((component, idx) => (
                                <Chip key={idx} label={component.type} size="small" variant="outlined" />
                              ))
                              : <Typography variant="body2" color="text.secondary">Sin componentes</Typography>
                            }
                            {template.components && template.components.length > 2 && (
                              <Chip label={`+${template.components.length - 2}`} size="small" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {template.wa_updated_at ? new Date(template.wa_updated_at).toLocaleDateString() : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Tooltip title="Ver Detalles">
                              <IconButton
                                size="small"
                                sx={{ color: '#667eea' }}
                              >
                                <FontAwesomeIcon icon={faEye} />
                              </IconButton>
                            </Tooltip>
                            {template.status === 'APPROVED' && (
                              <Tooltip title="Usar Template">
                                <IconButton
                                  size="small"
                                  sx={{ color: '#28a745' }}
                                >
                                  <FontAwesomeIcon icon={faCheck} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </StyledTableContainer>
          ) : (
            /* WhatsApp Templates Grid View */
            <Grid container spacing={3}>
              {waTemplates.length === 0 ? (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
                    <FontAwesomeIcon icon={faFileAlt} size="2x" style={{ color: '#ccc' }} />
                    <Typography variant="body1" color="text.secondary">
                      No se encontraron templates de WhatsApp
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Usa el botón "Sincronizar" para cargar templates desde WhatsApp Cloud API
                    </Typography>
                  </Box>
                </Grid>
              ) : (
                waTemplates.map((template, key) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={template.wa_template_id || key}>
                    <TemplateCard>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ 
                              bgcolor: template.status === 'APPROVED' ? '#28a745' : 
                                       template.status === 'PENDING' ? '#ffc107' : 
                                       template.status === 'REJECTED' ? '#dc3545' : '#6c757d',
                              width: 32, height: 32 
                            }}>
                              <FontAwesomeIcon icon={faComment} size="sm" />
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {template.name}
                            </Typography>
                          </Box>
                          <Chip 
                            label={template.status} 
                            color={
                              template.status === 'APPROVED' ? 'success' : 
                              template.status === 'PENDING' ? 'warning' : 
                              template.status === 'REJECTED' ? 'error' : 'default'
                            }
                            size="small"
                          />
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Categoría: {template.category}
                        </Typography>
                        
                        <Divider sx={{ my: 1 }} />
                        
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                          {template.components ? 
                            template.components.slice(0, 2).map((component, idx) => (
                              <Chip key={idx} label={component.type} size="small" variant="outlined" />
                            ))
                            : <Typography variant="caption" color="text.secondary">Sin componentes</Typography>
                          }
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary">
                            {template.wa_updated_at ? new Date(template.wa_updated_at).toLocaleDateString() : 'N/A'}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Ver Detalles">
                              <IconButton
                                size="small"
                                sx={{ color: '#667eea' }}
                              >
                                <FontAwesomeIcon icon={faEye} />
                              </IconButton>
                            </Tooltip>
                            {template.status === 'APPROVED' && (
                              <Tooltip title="Usar Template">
                                <IconButton
                                  size="small"
                                  sx={{ color: '#28a745' }}
                                >
                                  <FontAwesomeIcon icon={faCheck} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </TemplateCard>
                  </Grid>
                ))
              )}
            </Grid>
          )}
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {currentTitle}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.6fr 1fr' }, gap: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#fff' }}>
                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e5e7eb', backgroundColor: '#f8fafc' }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '15px' }}>General Information</Typography>
                </Box>
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Template Name"
                    value={formData.name || ""}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant={!useImageUpload ? "contained" : "outlined"}
                      onClick={() => setUseImageUpload(false)}
                    >
                      URL
                    </Button>
                    <Button
                      size="small"
                      variant={useImageUpload ? "contained" : "outlined"}
                      onClick={() => setUseImageUpload(true)}
                    >
                      Upload
                    </Button>
                  </Box>
                  {!useImageUpload ? (
                    <TextField
                      fullWidth
                      label="Header Image URL"
                      value={formData.image || ""}
                      onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                      helperText="Supports JPG/PNG URL"
                    />
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Button variant="outlined" component="label" disabled={imageUploading}>
                        {imageUploading ? "Subiendo..." : "Seleccionar imagen"}
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={uploadTemplateImage}
                        />
                      </Button>
                      <Typography variant="caption" color="text.secondary">
                        Solo imagenes (max 5MB). Tambien puedes volver a modo URL.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#fff' }}>
                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e5e7eb', backgroundColor: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '15px' }}>Message Content</Typography>
                  <Button size="small" variant="outlined" onClick={insertVariable}>Insert Variable</Button>
                </Box>
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={8}
                    label="Message Body"
                    value={formData.body || ""}
                    onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                    helperText={`${(formData.body || "").length} / 1024`}
                  />
                  <Autocomplete
                    multiple
                    freeSolo
                    options={variableValueOptions}
                    loading={variableFieldsLoading}
                    value={parameterChips}
                    onChange={(_, newValue) => {
                      const normalized = newValue
                        .map((item) => `${item}`.trim())
                        .filter(Boolean);
                      setParameterChips(normalized);
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option}
                          size="small"
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        label="Variable values"
                        placeholder="Type/select and press Enter"
                        helperText={`Campos sugeridos desde ${variableSourceTable}. Puedes escribir valores personalizados.`}
                      />
                    )}
                  />
                  <Box sx={{ border: '1px dashed #d1d5db', borderRadius: '10px', p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                        Composite variables
                      </Typography>
                      <Button size="small" variant="outlined" onClick={addCompositeVariable}>
                        Agregar composite
                      </Button>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      Define variables compuestas usando campos entre llaves, ejemplo: {'{Asegurado}'} - {'{Cedula}'}.
                    </Typography>
                    {compositeVariables.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        No hay composites configurados.
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {compositeVariables.map((item, index) => (
                          <Box key={`composite-${index}`} sx={{ border: '1px solid #e5e7eb', borderRadius: '8px', p: 1.5 }}>
                            <Grid container spacing={1}>
                              <Grid item xs={12} md={3}>
                                <TextField
                                  size="small"
                                  fullWidth
                                  label="Nombre"
                                  value={item.name || ""}
                                  onChange={(e) => updateCompositeVariable(index, "name", e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} md={7}>
                                <Autocomplete
                                  freeSolo
                                  options={variableValueOptions.map((option) => `{${option}}`)}
                                  openOnFocus
                                  selectOnFocus
                                  clearOnBlur={false}
                                  filterOptions={(options, state) => {
                                    const rawInput = `${state.inputValue || ""}`;
                                    const lastOpenBrace = rawInput.lastIndexOf("{");
                                    const lastCloseBrace = rawInput.lastIndexOf("}");
                                    const isTypingToken = lastOpenBrace > lastCloseBrace;

                                    // When typing after "{", suggest using only the current token fragment.
                                    if (isTypingToken) {
                                      const tokenFragment = rawInput.slice(lastOpenBrace + 1).toLowerCase().trim();
                                      if (!tokenFragment) return options;
                                      return options.filter((option) => {
                                        const optionName = `${option}`
                                          .replace(/^\{/, "")
                                          .replace(/\}$/, "")
                                          .toLowerCase();
                                        return optionName.includes(tokenFragment);
                                      });
                                    }

                                    const input = rawInput.toLowerCase().trim();
                                    if (!input) return options;
                                    return options.filter((option) => `${option}`.toLowerCase().includes(input));
                                  }}
                                  inputValue={item.expression || ""}
                                  onInputChange={(_, newInputValue) =>
                                    updateCompositeVariable(index, "expression", newInputValue)
                                  }
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      size="small"
                                      fullWidth
                                      label="Expresion"
                                      helperText="Sugerencias: escribe y selecciona campos como {Asegurado}, {Cedula}, etc."
                                    />
                                  )}
                                />
                              </Grid>
                              <Grid item xs={12} md={2}>
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => removeCompositeVariable(index)}
                                  sx={{ mt: { xs: 0, md: 0.5 } }}
                                >
                                  Eliminar
                                </Button>
                              </Grid>
                            </Grid>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                              Resultado preview: {renderCompositeExpressionPreview(item.expression) || "-"}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {variableTokens.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">No variables detected</Typography>
                    ) : (
                      variableTokens.map((token) => (
                        <Chip key={token} size="small" label={`{{${token}}}`} variant="outlined" />
                      ))
                    )}
                  </Box>
                  <Box sx={{ border: '1px dashed #d1d5db', borderRadius: '10px', p: 2 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '14px', mb: 1 }}>
                      Fixed values for unassigned variables
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      Si una variable no tiene valor en "Variable values" ni en "Comparacion de valores", asigna un valor fijo aqui.
                    </Typography>
                    {unassignedVariableTokens.length === 0 ? (
                      <Typography variant="caption" color="success.main">
                        Todas las variables detectadas tienen asignacion.
                      </Typography>
                    ) : (
                      <Grid container spacing={1}>
                        {unassignedVariableTokens.map((token) => (
                          <Grid item xs={12} md={6} key={`fixed-variable-${token}`}>
                            <TextField
                              size="small"
                              fullWidth
                              label={`Valor fijo {{${token}}}`}
                              value={fixedVariableValues[token] || ""}
                              onChange={(e) =>
                                setFixedVariableValues((prev) => ({
                                  ...prev,
                                  [token]: e.target.value,
                                }))
                              }
                              helperText="Requerido para guardar si no existe campo asignado."
                            />
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Box>

                  <Box sx={{ border: '1px dashed #d1d5db', borderRadius: '10px', p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                        Comparacion de valores
                      </Typography>
                      <Button size="small" variant="outlined" onClick={addComparacionValor}>
                        Agregar regla
                      </Button>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Compara una variable del template con un campo de ejecutivo y usa otro campo del ejecutivo para llenar otra variable.
                    </Typography>

                    {comparacionValores.length === 0 ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        No hay reglas configuradas.
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
                        {comparacionValores.map((rule, index) => (
                          <Box key={`comparacion-${index}`} sx={{ border: '1px solid #e5e7eb', borderRadius: '8px', p: 1.5 }}>
                            <Grid container spacing={1}>
                              <Grid item xs={12} md={3}>
                                <TextField
                                  select
                                  size="small"
                                  fullWidth
                                  label="Variable origen"
                                  SelectProps={{ native: true }}
                                  value={rule.variableOrigen || ""}
                                  onChange={(e) => updateComparacionValor(index, "variableOrigen", e.target.value)}
                                >
                                  {variableTokenOptions.map((token) => (
                                    <option key={`from-${token}`} value={token}>{`{{${token}}}`}</option>
                                  ))}
                                </TextField>
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <TextField
                                  select
                                  size="small"
                                  fullWidth
                                  label="Campo ejecutivo match"
                                  SelectProps={{ native: true }}
                                  value={rule.campoComparacionEjecutivo || ""}
                                  onChange={(e) => updateComparacionValor(index, "campoComparacionEjecutivo", e.target.value)}
                                >
                                  {ejecutivoFieldOptions.map((field) => (
                                    <option key={`cmp-${field}`} value={field}>{field}</option>
                                  ))}
                                </TextField>
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <TextField
                                  select
                                  size="small"
                                  fullWidth
                                  label="Variable destino"
                                  SelectProps={{ native: true }}
                                  value={rule.variableDestino || ""}
                                  onChange={(e) => updateComparacionValor(index, "variableDestino", e.target.value)}
                                >
                                  {variableTokenOptions.map((token) => (
                                    <option key={`to-${token}`} value={token}>{`{{${token}}}`}</option>
                                  ))}
                                </TextField>
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <TextField
                                  select
                                  size="small"
                                  fullWidth
                                  label="Campo ejecutivo valor"
                                  SelectProps={{ native: true }}
                                  value={rule.campoResultadoEjecutivo || ""}
                                  onChange={(e) => updateComparacionValor(index, "campoResultadoEjecutivo", e.target.value)}
                                >
                                  {ejecutivoFieldOptions.map((field) => (
                                    <option key={`val-${field}`} value={field}>{field}</option>
                                  ))}
                                </TextField>
                              </Grid>
                            </Grid>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                              <Button
                                size="small"
                                color="error"
                                onClick={() => removeComparacionValor(index)}
                              >
                                Eliminar
                              </Button>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ border: '1px dashed #d1d5db', borderRadius: '10px', p: 2 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '14px', mb: 1 }}>
                      Asignacion de ejecutivo
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      Selecciona las variables del mensaje para nombre e id del ejecutivo. Opcionalmente puedes elegir una variable para el nombre del cliente.
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={3}>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          label="Variable ejecutivo nombre"
                          SelectProps={{ native: true }}
                          value={asignacionEjecutivo.variableEjecutivoNombre || ""}
                          onChange={(e) =>
                            setAsignacionEjecutivo((prev) => ({
                              ...prev,
                              variableEjecutivoNombre: Number(e.target.value) || "",
                            }))
                          }
                        >
                          <option value="">Sin asignar</option>
                          {variableTokenOptions.map((token) => (
                            <option key={`ej-name-${token}`} value={token}>{`{{${token}}}`}</option>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          label="Campo ejecutivo id"
                          SelectProps={{ native: true }}
                          value={asignacionEjecutivo.campoEjecutivoId || ""}
                          onChange={(e) =>
                            setAsignacionEjecutivo((prev) => ({
                              ...prev,
                              campoEjecutivoId: e.target.value || "",
                            }))
                          }
                        >
                          <option value="">Sin asignar</option>
                          {ejecutivoFieldOptions.map((field) => (
                            <option key={`ej-id-field-${field}`} value={field}>{field}</option>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          label="Fallback ejecutivo"
                          SelectProps={{ native: true }}
                          value={asignacionEjecutivo.fallbackEjecutivoId || ""}
                          onChange={(e) =>
                            setAsignacionEjecutivo((prev) => ({
                              ...prev,
                              fallbackEjecutivoId: e.target.value || "",
                            }))
                          }
                        >
                          <option value="">Sin fallback</option>
                          {ejecutivoOptions.map((ejecutivo) => (
                            <option key={`fallback-ej-${ejecutivo.value}`} value={ejecutivo.value}>
                              {ejecutivo.label}
                            </option>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          label="Variable nombre cliente (opcional)"
                          SelectProps={{ native: true }}
                          value={asignacionEjecutivo.variableNombreCliente || ""}
                          onChange={(e) =>
                            setAsignacionEjecutivo((prev) => ({
                              ...prev,
                              variableNombreCliente: Number(e.target.value) || "",
                            }))
                          }
                        >
                          <option value="">Sin asignar</option>
                          {variableTokenOptions.map((token) => (
                            <option key={`client-name-${token}`} value={token}>{`{{${token}}}`}</option>
                          ))}
                        </TextField>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 600, mb: 1.5 }}>Live Preview</Typography>
              <Box sx={{ mx: 'auto', width: 300, height: 580, borderRadius: '38px', border: '8px solid #0f172a', backgroundColor: '#111827', overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
                <Box sx={{ height: '100%', backgroundColor: '#E5DDD5', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ backgroundColor: '#075E54', color: 'white', px: 2, py: 1.5, fontWeight: 600, fontSize: '14px' }}>
                    Acme Business
                  </Box>
                  <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'flex-start' }}>
                    <Box sx={{ maxWidth: '92%', backgroundColor: 'white', borderRadius: '10px', borderTopLeftRadius: '3px', overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
                      {formData.image && (
                        <Box sx={{ width: '100%', aspectRatio: '1.91/1', backgroundColor: '#e5e7eb' }}>
                          <img src={formData.image} alt="Template preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </Box>
                      )}
                      <Box sx={{ p: 1.5 }}>
                        <Typography sx={{ fontSize: '13px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5 }}>
                          {previewBody || "Type your WhatsApp message here..."}
                        </Typography>
                        <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed #e5e7eb' }}>
                          <Typography sx={{ fontSize: '11px', color: '#475569' }}>
                            Ejecutivo nombre: {asignacionEjecutivo.variableEjecutivoNombre ? `{{${asignacionEjecutivo.variableEjecutivoNombre}}}` : "Sin asignar"}
                          </Typography>
                          <Typography sx={{ fontSize: '11px', color: '#475569' }}>
                            Campo ejecutivo id: {asignacionEjecutivo.campoEjecutivoId || "Sin asignar"}
                          </Typography>
                          <Typography sx={{ fontSize: '11px', color: '#475569' }}>
                            Fallback ejecutivo: {asignacionEjecutivo.fallbackEjecutivoId || "Sin fallback"}
                          </Typography>
                          <Typography sx={{ fontSize: '11px', color: '#475569' }}>
                            Nombre cliente: {asignacionEjecutivo.variableNombreCliente ? `{{${asignacionEjecutivo.variableNombreCliente}}}` : "Sin asignar"}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button onClick={submitTemplate} variant="contained" disabled={modalSubmitting || imageUploading}>
            {modalSubmitting ? 'Guardando...' : currentButtonText}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
}
